/**
 * GDPR self-service (LEG-2): everything a business holds here as one ZIP, and deleting a business
 * or the whole account.
 *
 * The export is lossless rather than pretty: every record as JSON and every stored file as it was
 * uploaded or received. The accountant-ready Belege per period come from /close.
 * Deleting goes through server routes (`pb_hooks/account.pb.js`) that ask for the typed name.
 */
import { zipSync, type Zippable } from "fflate";
import { fileSafe } from "@/lib/export/period-package";
import { pb } from "@/lib/platform/pocketbase";

/** Every collection that belongs to a business, in the order a reader would want them. */
export const EXPORT_COLLECTIONS = [
  "memberships",
  "invites",
  "categories",
  "documents",
  "bank_accounts",
  "transactions",
  "statement_imports",
  "inbound_messages",
  "partner_accounts",
  "journal_entries",
  "journal_lines",
  "assets",
  "trips",
  "booking_rules",
] as const;

type ExportRecord = Record<string, unknown> & { id: string; collectionName?: string };

export type DataExportInput = {
  workspace: ExportRecord & { name: string };
  /** The person exporting: their own profile goes in too (Art. 15 GDPR). */
  profile: ExportRecord;
  /** Explains the folders, in the person's language. */
  readme: string;
  list?: (collection: string, workspaceId: string) => Promise<ExportRecord[]>;
  fetchFile?: (record: ExportRecord, fileName: string) => Promise<Uint8Array>;
  onProgress?: (done: number, total: number) => void;
};

const listAll = (collection: string, workspaceId: string) =>
  pb.collection(collection).getFullList<ExportRecord>({
    filter: pb.filter("workspace = {:workspaceId}", { workspaceId }),
    // Journal lines carry no timestamp of their own; they follow their entry.
    sort: collection === "journal_lines" ? "entry" : "created",
  });

const fetchStored = async (record: ExportRecord, fileName: string) => {
  const token = await pb.files.getToken();
  const response = await fetch(pb.files.getURL(record, fileName, { token }));
  if (!response.ok) throw new Error(`${fileName}: ${response.status}`);
  return new Uint8Array(await response.arrayBuffer());
};

const fileNames = (value: unknown): string[] =>
  (Array.isArray(value) ? value : [value]).filter(
    (name): name is string => typeof name === "string" && name !== "",
  );

/** The fields that are only meaningful to the server (auth tokens never leave it anyway). */
const withoutInternals = ({ expand: _expand, ...record }: ExportRecord) => record;

export async function buildDataExport(input: DataExportInput): Promise<Uint8Array> {
  const list = input.list ?? listAll;
  const fetchFile = input.fetchFile ?? fetchStored;
  const encoder = new TextEncoder();
  const json = (value: unknown) => encoder.encode(JSON.stringify(value, null, 2));
  const files: Zippable = {
    "README.txt": encoder.encode(input.readme),
    "data/workspace.json": json(withoutInternals(input.workspace)),
    "data/profile.json": json(withoutInternals(input.profile)),
  };

  const records: Partial<Record<(typeof EXPORT_COLLECTIONS)[number], ExportRecord[]>> = {};
  for (const collection of EXPORT_COLLECTIONS) {
    records[collection] = await list(collection, input.workspace.id);
    files[`data/${collection}.json`] = json(records[collection]!.map(withoutInternals));
  }

  // The files: a folder per Beleg named by its Belegnummer, the emails as they arrived.
  const wanted: { record: ExportRecord; name: string; path: string }[] = [];
  for (const document of records.documents ?? []) {
    const folder = `files/documents/${fileSafe(String(document.docNumber || document.id), 60)}`;
    for (const field of ["file", "originals", "attachments"]) {
      for (const name of fileNames(document[field])) {
        wanted.push({ record: document, name, path: `${folder}/${name}` });
      }
    }
  }
  for (const message of records.inbound_messages ?? []) {
    for (const name of fileNames(message.raw)) {
      wanted.push({ record: message, name, path: `files/emails/${message.id}_${name}` });
    }
  }
  for (const [index, file] of wanted.entries()) {
    // Already compressed (PDF, JPEG) or small: stored, which keeps a large export fast.
    files[file.path] = [await fetchFile(file.record, file.name), { level: 0 }];
    input.onProgress?.(index + 1, wanted.length);
  }

  return zipSync(files);
}

/** `Alpenblick-Design-daten-2026-10-10.zip` */
export function dataExportName(workspaceName: string, today = new Date()): string {
  return `${fileSafe(workspaceName) || "export"}-data-${today.toISOString().slice(0, 10)}.zip`;
}

/** Deletes the business and everything in it. `name` must be the business name as typed. */
export async function deleteWorkspace(workspaceId: string, name: string): Promise<void> {
  await pb.send(`/api/workspaces/${encodeURIComponent(workspaceId)}/delete`, {
    method: "POST",
    body: { name },
  });
}

/** Deletes every business the person owns, their memberships elsewhere and the account. */
export async function deleteAccount(email: string): Promise<void> {
  await pb.send("/api/account/delete", { method: "POST", body: { email } });
}

/** The server's reason for refusing a delete, for a message that says what to do. */
export function deleteRefusal(error: unknown): "mismatch" | "subscription" | null {
  const code = (error as { response?: { code?: string } })?.response?.code;
  if (code === "name_mismatch" || code === "email_mismatch") return "mismatch";
  if (code === "subscription_active") return "subscription";
  return null;
}
